import React from 'react'
import {
    Box,
    Flex,
    Heading,
    Text,
    Button,
    Container,
    VStack,
    Link,
    Divider,
    useToken,
} from '@chakra-ui/react'
import { Icon as CustomIcon, IconType } from '../Icon'

interface ComingSoonFeatureProps {
    title: string
    description?: string
    calendarLink?: string
    featureIcon?: IconType
    backgroundImage?: string
    feature?: string
    buttonText?: string
}

export const ComingSoonFeature: React.FC<ComingSoonFeatureProps> = ({
    title,
    description = "We're working on something exciting! This feature is currently in development.",
    calendarLink = 'https://calendly.com/your-calendar-link',
    featureIcon = 'analytics',
    backgroundImage = '/coming-soon.png',
    buttonText = 'Chat with our Product Team',
}) => {
    // Get theme colors
    const [
        brandPrimary,
        brandWhite,
        brandSecondaryPink,
        brandSecondaryBlue,
        brandSecondaryGreen,
    ] = useToken('colors', [
        'brand.primary',
        'brand.white',
        'brand.secondary.pink',
        'brand.secondary.blue',
        'brand.secondary.green',
    ])

    return (
        <Container
            maxW="full"
            p={0}
            h="100vh"
            position="relative"
            overflow="hidden"
        >
            {/* Blurred Background Image */}
            <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                zIndex={0}
                backgroundImage={`url(${backgroundImage})`}
                backgroundSize="cover"
                backgroundPosition="center"
                backgroundRepeat="no-repeat"
                filter="blur(6px)"
                opacity={0.2}
                transform="scale(1.1)" // Prevents blur from showing edges
                pointerEvents="none"
            />

            {/* Content */}
            <Flex
                position="relative"
                zIndex={1}
                h="full"
                alignItems="center"
                justifyContent="center"
                p={{ base: 4, md: 8 }}
            >
                <Box
                    bg="brand.primary" // Black background
                    borderRadius="xl"
                    p={{ base: 4, md: 6 }}
                    maxW="550px"
                    width="full"
                    textAlign="center"
                    borderWidth="1px"
                    borderColor="rgba(255, 255, 255, 0.1)"
                    position="relative"
                    overflow="hidden"
                    transition="all 0.2s ease-out"
                    boxShadow="lg"
                    _hover={{
                        transform: 'translateY(-2px)',
                        boxShadow: 'xl',
                    }}
                >
                    {/* Decorative Elements - Vibrant Gradients */}
                    <Box
                        position="absolute"
                        top="-20px"
                        right="-20px"
                        width="100px"
                        height="100px"
                        borderRadius="full"
                        bg={`linear-gradient(135deg, ${brandSecondaryPink}40, ${brandSecondaryBlue}40)`}
                        zIndex={0}
                    />
                    <Box
                        position="absolute"
                        bottom="-30px"
                        left="-30px"
                        width="120px"
                        height="120px"
                        borderRadius="full"
                        bg={`linear-gradient(135deg, ${brandSecondaryGreen}40, ${brandSecondaryBlue}40)`}
                        zIndex={0}
                    />

                    {/* Additional decorative elements for more vibrancy */}
                    <Box
                        position="absolute"
                        top="30%"
                        right="-40px"
                        width="80px"
                        height="80px"
                        borderRadius="full"
                        bg={`linear-gradient(135deg, ${brandSecondaryPink}30, ${brandSecondaryGreen}30)`}
                        zIndex={0}
                        opacity={0.6}
                    />
                    <Box
                        position="absolute"
                        bottom="40%"
                        left="-30px"
                        width="60px"
                        height="60px"
                        borderRadius="full"
                        bg={`linear-gradient(135deg, ${brandSecondaryBlue}30, ${brandSecondaryPink}30)`}
                        zIndex={0}
                        opacity={0.6}
                    />

                    {/* Content */}
                    <VStack
                        spacing={{ base: 3, md: 4 }}
                        position="relative"
                        zIndex={1}
                    >
                        <Flex
                            bg={`linear-gradient(135deg, ${brandSecondaryGreen}, ${brandSecondaryBlue})`}
                            w="60px"
                            h="60px"
                            borderRadius="full"
                            alignItems="center"
                            justifyContent="center"
                            mb={1}
                        >
                            <CustomIcon
                                type={featureIcon}
                                width={28}
                                height={28}
                                color={brandWhite}
                            />
                        </Flex>

                        <Heading size="md" fontWeight="bold" color={brandWhite}>
                            {title}
                        </Heading>

                        <Text
                            fontSize="sm"
                            color={brandWhite}
                            maxW="400px"
                            noOfLines={3}
                        >
                            {description}
                        </Text>

                        <Divider borderColor="rgba(255, 255, 255, 0.2)" />

                        <VStack spacing={3} width="full">
                            <Text
                                fontWeight="medium"
                                fontSize="sm"
                                color={brandWhite}
                            >
                                Interested in this feature?
                            </Text>

                            <Link
                                href={calendarLink}
                                isExternal
                                _hover={{ textDecoration: 'none' }}
                            >
                                <Button
                                    size="md"
                                    width="full"
                                    height="48px"
                                    borderRadius="lg"
                                    fontWeight="semibold"
                                    bg={brandSecondaryGreen}
                                    color={brandPrimary}
                                    _hover={{
                                        bg: 'brand.tints.green.75',
                                        transform: 'translateY(-2px)',
                                    }}
                                >
                                    <Flex alignItems="center">
                                        <Box mr={2}>
                                            <CustomIcon
                                                type="info"
                                                width={18}
                                                height={18}
                                                color={brandPrimary}
                                            />
                                        </Box>
                                        {buttonText}
                                    </Flex>
                                </Button>
                            </Link>
                        </VStack>
                    </VStack>
                </Box>
            </Flex>
        </Container>
    )
}

export default ComingSoonFeature
